import { format } from "date-fns";
import { useMemo, useRef } from "react";
import {
  MessageGroup,
  MessageType,
  getMessageTimestamp,
} from "../interfaces/all";

const groupMessagesByDate = (messages: MessageType[]): MessageGroup[] => {
  if (!messages.length) {
    return [];
  }

  // Usar um Map para rastrear mensagens já processadas
  const processedMessages = new Map<string, MessageType>();
  const groups = new Map<string, MessageType[]>();

  // Log apenas uma vez por conjunto de mensagens
  const messageIds = messages.map((m) => m._id).filter(Boolean);
  const uniqueIds = [...new Set(messageIds)];

  if (uniqueIds.length !== messageIds.length) {
    console.warn("Mensagens duplicadas detectadas:", {
      total: messages.length,
      unique: uniqueIds.length,
      ids: uniqueIds,
      processedAt: new Date().toISOString(),
    });
  }

  for (const message of messages) {
    try {
      // Criar uma chave única para a mensagem
      const messageKey = `${message._id || message.timestamp}-${message.from}-${message.to}`;

      // Pular se a mensagem já foi processada
      if (processedMessages.has(messageKey)) {
        continue;
      }

      // Marcar mensagem como processada
      processedMessages.set(messageKey, message);

      // Validar timestamp antes de processar
      const messageTimestamp = getMessageTimestamp(message);
      if (!messageTimestamp || isNaN(messageTimestamp.getTime())) {
        console.warn("Mensagem com timestamp inválido ignorada:", {
          id: message._id,
          messageTimestamp: messageTimestamp,
          processedAt: new Date().toISOString(),
        });
        continue;
      }

      const date = format(messageTimestamp, "dd/MM/yyyy");
      if (!groups.has(date)) {
        groups.set(date, []);
      }
      groups.get(date)?.push(message);
    } catch (error) {
      console.error("Erro ao processar mensagem:", {
        id: message._id,
        error,
        messageTimestamp: message.timestamp,
        processedAt: new Date().toISOString(),
      });
    }
  }

  return Array.from(groups.entries()).map(([date, messages]) => ({
    date,
    messages: messages.sort(
      (a, b) =>
        getMessageTimestamp(a).getTime() - getMessageTimestamp(b).getTime(),
    ),
  }));
};

export const useMessageGrouping = (messages: MessageType[]) => {
  const prevMessagesRef = useRef<MessageType[]>([]);
  const prevGroupsRef = useRef<MessageGroup[]>([]);
  const prevHashRef = useRef<string>("");

  return useMemo(() => {
    // Se não houver mensagens, retornar array vazio
    if (!messages.length) {
      prevMessagesRef.current = [];
      prevGroupsRef.current = [];
      prevHashRef.current = "";
      return { messageGroups: [] };
    }

    // Calcular hash das mensagens usando apenas IDs únicos e timestamps
    const uniqueIds = [...new Set(messages.map((m) => m._id).filter(Boolean))];
    const timestamps = messages
      .map((m) => getMessageTimestamp(m).getTime())
      .join(",");
    const currentHash = `${uniqueIds.join("|")}-${timestamps}`;

    // Se o hash não mudou, retornar os grupos anteriores
    if (currentHash === prevHashRef.current) {
      return { messageGroups: prevGroupsRef.current };
    }

    // Processar mensagens
    const groups = groupMessagesByDate(messages);

    // Atualizar refs
    prevMessagesRef.current = messages;
    prevGroupsRef.current = groups;
    prevHashRef.current = currentHash;

    return { messageGroups: groups };
  }, [messages]);
};
